// Expirations tab -- licenses and CPR cards from public.onboarding_expirations
// (latest document per candidate / kind / state; security_invoker, so its rows
// are the caller's allowlisted RLS rows). Discipline comes from the candidate
// record already loaded in the CRM. Nothing here exports or downloads.
//
// "Record renewal" (hub ruling 2026-10-10): the expiry reminder asks the candidate to REPLY with a photo or PDF of the
// renewal; staff record it here. With a file: it goes into the private bucket and becomes a NEW license / CPR row (the
// view then shows it; the old row and file stay as history). Without one: the current row's date moves, only if it is
// still the date shown (a stale screen changes nothing). Either way the new date must be later than today and than the
// current one, and the candidate's activity gets one line (dates and kinds only). Pending reminder drafts for the old
// date are then stale: the CRM refuses to approve them and pt-agents rejects them on its next run.
import { daysLeft, isRed } from './onboarding';

export const KIND_LABELS = { license: 'License', cpr: 'CPR card' };

export function makeExpirationsApi(supabase) {
  return {
    // -> { allowed:false } or { allowed:true, rows }
    async load() {
      // A read-only allowlist probe with no side effects: the staff RPC raises
      // 42501 for a login that is not on the allowlist (the view itself would
      // just return no rows, indistinguishable from "nothing expiring").
      const probe = await supabase.rpc('onboarding_decrypt_log', { p_candidate_id: 0 });
      if (probe.error) {
        if (probe.error.code === '42501' || /allowlist|staff_only/.test(probe.error.message || '')) return { allowed: false };
        throw new Error(probe.error.message);
      }
      const { data, error } = await supabase.from('onboarding_expirations')
        .select('candidate_id,candidate_name,recruiter,kind,license_state,expires_on,days_left,document_id');
      if (error) throw new Error(error.message);
      return { allowed: true, rows: data };
    },
    // -> { mode: 'file' | 'date' }; throws an Error whose message is shown to staff.
    async recordRenewal(row, { expiresOn, file = null, by = '' } = {}, now = new Date()) {
      const bad = renewalProblem(row, { expiresOn, file }, now);
      if (bad) throw new Error(bad);
      const what = `${KIND_LABELS[row.kind] || row.kind}${row.license_state ? ` (${row.license_state})` : ''}`;
      let mode;
      if (file) {
        const ext = RENEWAL_TYPES[file.type];
        const path = `${row.candidate_id}/renewal/${now.getTime()}-${row.kind}.${ext}`;
        const up = await supabase.storage.from('onboarding-docs').upload(path, file, { upsert: false, contentType: file.type });
        if (up.error) throw new Error(`The file could not be stored: ${up.error.message}`);
        const ins = await supabase.from('onboarding_documents').insert({ candidate_id: row.candidate_id, kind: row.kind, storage_path: path,
          license_state: row.license_state || null, expires_on: expiresOn, uploaded_by: by ? `staff ${by}` : 'staff' }).select('id');
        if (ins.error) {
          await supabase.storage.from('onboarding-docs').remove([path]).catch(() => {});
          throw new Error(`The renewal could not be recorded: ${ins.error.message}`);
        }
        mode = 'file';
      } else {
        const upd = await supabase.from('onboarding_documents').update({ expires_on: expiresOn })
          .eq('id', row.document_id).eq('expires_on', row.expires_on).select('id');
        if (upd.error) throw new Error(`The renewal could not be recorded: ${upd.error.message}`);
        if (!upd.data || upd.data.length !== 1) throw new Error('This expiration changed elsewhere -- reload and try again. Nothing was changed.');
        mode = 'date';
      }
      const note = `${what} renewal recorded: expires ${expiresOn} (${mode === 'file' ? 'new file attached' : 'date only, no new file'})${by ? ` by ${by}` : ''}`;
      const log = await supabase.from('activity_log').insert({ candidate_id: row.candidate_id, date: now.toISOString().slice(0, 10), type: 'onboarding', note });
      if (log.error) throw new Error(`The renewal was recorded, but the activity log could not be written: ${log.error.message}`);
      return { mode };
    },
  };
}

export const RENEWAL_TYPES = { 'application/pdf': 'pdf', 'image/jpeg': 'jpg', 'image/png': 'png' };
export const RENEWAL_MAX_BYTES = 15 * 1024 * 1024;
/** Why this renewal cannot be recorded (null = it can). */
export function renewalProblem(row, { expiresOn, file = null } = {}, now = new Date()) {
  if (!row || !row.document_id) return 'This expiration cannot be renewed here (no document on file).';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(expiresOn || '')) || Number.isNaN(Date.parse(`${expiresOn}T00:00:00Z`))) return 'Enter the new expiration date.';
  if (expiresOn <= now.toISOString().slice(0, 10)) return 'The new expiration date must be after today.';
  if (row.expires_on && expiresOn <= String(row.expires_on)) return 'The new expiration date must be later than the current one.';
  if (file && !RENEWAL_TYPES[file.type]) return 'The file must be a PDF, JPG or PNG.';
  if (file && file.size > RENEWAL_MAX_BYTES) return 'The file is over 15 MB.';
  return null;
}

// rows + candidates -> display rows, soonest first, with discipline and red flag
export function shape(rows, candidates, now = new Date()) {
  const byId = new Map((candidates || []).map((c) => [c.id, c]));
  return (rows || []).map((r) => {
    const days = daysLeft(r.expires_on, now);
    const cand = byId.get(r.candidate_id);
    return {
      ...r,
      days,
      red: isRed(days),
      discipline: (cand && cand.specialty) || '',
      recruiter: r.recruiter || (cand && cand.recruiter) || '',
    };
  }).sort((a, b) => a.days - b.days || String(a.candidate_name).localeCompare(String(b.candidate_name)));
}

export const ANY = '';
export function applyFilters(rows, { discipline = ANY, recruiter = ANY } = {}) {
  return rows.filter((r) => (discipline === ANY || r.discipline === discipline) && (recruiter === ANY || r.recruiter === recruiter));
}
export function options(rows, key) {
  return [...new Set(rows.map((r) => r[key]).filter(Boolean))].sort();
}
export function daysText(days) {
  if (days === null) return '';
  if (days < 0) return `expired ${-days} day${days === -1 ? '' : 's'} ago`;
  if (days === 0) return 'expires today';
  return `${days} day${days === 1 ? '' : 's'} left`;
}
